/**
 * Session Store
 *
 * Manages session timeout warnings and automatic logout.
 * Replaces Alpine.store('app') session-related functionality.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from './auth'

console.log('🔥 SESSION STORE LOADED - NEW VERSION 🔥')

export const useSessionStore = defineStore('session', () => {
  // ==================== STATE ====================

  /**
   * Is the session warning modal currently shown?
   * Replaces: Alpine.store('app').sessionWarningShown
   */
  const warningShown = ref(false)

  /**
   * Is the session expired modal currently shown?
   */
  const expiredShown = ref(false)

  /**
   * Countdown seconds remaining (for warning modal)
   */
  const countdownSeconds = ref(120) // Will be calculated dynamically in showWarning()

  /**
   * Session timeout constants (in milliseconds)
   * Replaces: Alpine.store('app').WARNING_TIME and TIMEOUT_TIME
   */
  const WARNING_TIME = 28 * 60 * 1000  // 28 minutes
  const TIMEOUT_TIME = 30 * 60 * 1000  // 30 minutes

  // ==================== PRIVATE TIMERS ====================

  let warningTimer = null
  let timeoutTimer = null
  let countdownInterval = null
  let isInitialized = false

  // ==================== ACTIONS ====================

  /**
   * Show Warning Modal
   * Called when user has been inactive for WARNING_TIME
   * Replaces: Alpine.store('app').showWarningModal()
   */
  function showWarning() {
    const authStore = useAuthStore()

    console.log('[SESSION] showWarning called, isSignedIn:', authStore.isSignedIn)

    // Only show if user is signed in
    if (!authStore.isSignedIn) {
      return
    }

    warningShown.value = true
    // Calculate countdown from time difference between warning and timeout
    countdownSeconds.value = Math.floor((TIMEOUT_TIME - WARNING_TIME) / 1000)

    console.log('[SESSION] Warning modal shown, countdown:', countdownSeconds.value)

    // Start countdown
    countdownInterval = setInterval(() => {
      countdownSeconds.value--

      if (countdownSeconds.value <= 0) {
        clearInterval(countdownInterval)
      }
    }, 1000)
  }

  /**
   * Hide Warning Modal
   * Called when user clicks "Keep Working"
   */
  function hideWarning() {
    warningShown.value = false

    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }

  /**
   * Keep Working - User wants to continue session
   * Refreshes the session and resets timers
   */
  async function keepWorking() {
    const authStore = useAuthStore()

    try {
      // Refresh session on backend
      await authStore.refreshSession()

      // Hide warning modal
      hideWarning()

      // Reset timers
      resetTimers()

    } catch (error) {
      console.error('Failed to refresh session:', error)
      // Continue anyway - let backend handle it
      hideWarning()
      resetTimers()
    }
  }

  /**
   * Handle Session Timeout
   * Called when user has been inactive for TIMEOUT_TIME
   * Replaces: Alpine.store('app').handleSessionTimeout()
   */
  async function handleTimeout() {
    const authStore = useAuthStore()

    console.log('[SESSION] handleTimeout called')

    // Hide warning modal if shown
    hideWarning()

    // Sign out user
    await authStore.signOut()

    // Show expired modal
    expiredShown.value = true

    // Clear all timers
    clearAllTimers()
  }

  /**
   * Reset Timers - Restart activity monitoring
   * Called on user activity (click, scroll, keypress, etc.)
   * Replaces: Alpine.store('app').setupSessionTimeout() reset logic
   */
  function resetTimers() {
    const authStore = useAuthStore()

    console.log('[SESSION] resetTimers called, isSignedIn:', authStore.isSignedIn)

    // Clear existing timers
    clearAllTimers()

    // Only set timers if user is signed in
    if (!authStore.isSignedIn) {
      console.log('[SESSION] User not signed in, skipping timer setup')
      return
    }

    // Set warning timer (28 minutes)
    warningTimer = setTimeout(() => {
      showWarning()
    }, WARNING_TIME)

    // Set timeout timer (30 minutes)
    timeoutTimer = setTimeout(() => {
      handleTimeout()
    }, TIMEOUT_TIME)

    console.log('[SESSION] Timers set - warning in', WARNING_TIME / 1000, 'seconds, timeout in', TIMEOUT_TIME / 1000, 'seconds')
  }

  /**
   * Clear All Timers
   * Helper function to clean up all active timers
   */
  function clearAllTimers() {
    if (warningTimer) {
      clearTimeout(warningTimer)
      warningTimer = null
    }

    if (timeoutTimer) {
      clearTimeout(timeoutTimer)
      timeoutTimer = null
    }

    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }

  /**
   * Initialize Session Monitoring
   * Sets up activity listeners and starts timers
   * Replaces: Alpine.store('app').setupSessionTimeout()
   *
   * Should be called once on app initialization (in App.vue)
   */
  function initializeSessionMonitoring() {
    console.log('[SESSION] initializeSessionMonitoring called')

    // Prevent multiple initializations
    if (isInitialized) {
      console.log('[SESSION] Already initialized, skipping')
      return
    }

    // Activity events to monitor
    const events = ['click', 'scroll', 'keypress', 'mousemove']

    // Add event listeners
    events.forEach(eventType => {
      window.addEventListener(eventType, resetTimers)
    })

    console.log('[SESSION] Event listeners added for:', events.join(', '))

    // Mark as initialized
    isInitialized = true

    // Start initial timers
    resetTimers()
  }

  /**
   * Cleanup - Remove event listeners and timers
   * Should be called when app is destroyed (unlikely in SPA)
   */
  function cleanup() {
    const events = ['click', 'scroll', 'keypress', 'mousemove']

    events.forEach(eventType => {
      window.removeEventListener(eventType, resetTimers)
    })

    clearAllTimers()
    isInitialized = false
    console.log('[SESSION] Cleanup complete, isInitialized reset')
  }

  /**
   * Dismiss Expired Modal
   * User acknowledges session expiration
   */
  function dismissExpired() {
    expiredShown.value = false
  }

  // ==================== RETURN (PUBLIC API) ====================

  return {
    // State
    warningShown,
    expiredShown,
    countdownSeconds,
    WARNING_TIME,
    TIMEOUT_TIME,

    // Actions
    showWarning,
    hideWarning,
    keepWorking,
    handleTimeout,
    resetTimers,
    initializeSessionMonitoring,
    cleanup,
    dismissExpired
  }
})
